import { useState, useEffect } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import Input from '../input/Input';
import DropDown from '../dropdown/DropDown';
import P from '../texts/p/P';
import { toast } from 'react-toastify';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import { 
  getPartnerFormMetadata, 
  createPartner,
  updatePartner,
} from '../../services/GuaranteesService';

interface HierarchyLevel {
  id: number;
  name: string;
}

interface FormMetadata {
  hierarchy_levels: HierarchyLevel[];
}

/** Fila seleccionada para edición; null/undefined = alta */
export interface PartnerToEdit {
  id: number;
  name: string;
  cuit: string;
  share_quantity: number;
  hierarchy_level_id: number;
}

interface NewPartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  partnerToEdit?: PartnerToEdit | null;
}

const NewPartnerModal = ({
  isOpen,
  onClose,
  onSuccess,
  partnerToEdit = null,
}: NewPartnerModalProps) => {
  const fiscalId = useFiscalIdConfig();
  const [formMetadata, setFormMetadata] = useState<FormMetadata>({
    hierarchy_levels: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);

  // Form values
  const [name, setName] = useState<string>('');
  const [cuit, setCuit] = useState<string>('');
  const [shareQuantity, setShareQuantity] = useState<string>('');
  const [hierarchyLevelId, setHierarchyLevelId] = useState<number | null>(null);

  const isEditMode = Boolean(partnerToEdit);

  const resetForm = () => {
    setName('');
    setCuit('');
    setShareQuantity('');
    setHierarchyLevelId(null);
  };

  useEffect(() => {
    if (isOpen) {
      loadFormMetadata();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (partnerToEdit) {
      setName(partnerToEdit.name ?? '');
      setCuit(fiscalId.format(partnerToEdit.cuit ?? ''));
      setShareQuantity(
        partnerToEdit.share_quantity != null ? String(partnerToEdit.share_quantity) : ''
      );
      setHierarchyLevelId(partnerToEdit.hierarchy_level_id ?? null);
    } else {
      resetForm();
    }
  }, [isOpen, partnerToEdit]);

  const loadFormMetadata = async () => {
    setIsLoadingMetadata(true);
    try {
      const response = await getPartnerFormMetadata();
      if (response.success) {
        if ('data' in response && response.data) {
          setFormMetadata(response.data);
        }
      } else {
        const errorMsg = 'message' in response ? response.message : 'Error al cargar datos del formulario';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error loading form metadata:', error);
      toast.error('Error al cargar datos del formulario');
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const handleCuitChange = (value: string) => {
    setCuit(fiscalId.format(value));
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    // Validations
    if (!name || name.trim() === '') {
      toast.error('Ingrese el nombre del socio');
      return;
    }

    if (!cuit || cuit.trim() === '') {
      toast.error(fiscalId.requiredMessage);
      return;
    }

    const cuitDigits = cuit.replace(/\D/g, '');
    if (!fiscalId.isValidDigits(cuitDigits)) {
      toast.error(fiscalId.invalidLengthMessage);
      return;
    }

    if (!shareQuantity || isNaN(parseFloat(shareQuantity)) || parseFloat(shareQuantity) <= 0) {
      toast.error('Ingrese una cantidad de acciones válida');
      return;
    }

    if (!hierarchyLevelId) {
      toast.error('Seleccione un rol jerárquico');
      return;
    }

    setIsLoading(true);
    try {
      const cuitForRequest = isEditMode && partnerToEdit
        ? partnerToEdit.cuit.replace(/\D/g, '')
        : cuit.replace(/\D/g, '');

      const requestBody = {
        name: name.trim(),
        cuit: cuitForRequest,
        share_quantity: parseInt(shareQuantity, 10),
        hierarchy_level_id: hierarchyLevelId,
      };

      const response = isEditMode && partnerToEdit
        ? await updatePartner(partnerToEdit.id, requestBody)
        : await createPartner(requestBody);

      if (response.success) {
        toast.success(isEditMode ? 'Socio actualizado exitosamente' : 'Socio creado exitosamente');
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMsg =
          'message' in response
            ? response.message
            : isEditMode
              ? 'Error al actualizar socio'
              : 'Error al crear socio';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error(isEditMode ? 'Error updating partner:' : 'Error creating partner:', error);
      toast.error(isEditMode ? 'Error al actualizar socio' : 'Error al crear socio');
    } finally {
      setIsLoading(false);
    }
  };

  // Dropdown helpers for Rol jerárquico
  const hierarchyLevelItems = (formMetadata.hierarchy_levels || []).map(level => ({
    label: level.name,
    disabled: false
  }));

  const getSelectedHierarchyLevelName = () => {
    const selected = formMetadata.hierarchy_levels?.find(level => level.id === hierarchyLevelId);
    return selected ? selected.name : 'Seleccionar';
  };

  const handleHierarchyLevelChange = (selectedLabel: string) => {
    const level = formMetadata.hierarchy_levels?.find(level => level.name === selectedLabel);
    if (level) {
      setHierarchyLevelId(level.id);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Editar socio' : 'Añadir nuevo socio'}
      width='600px'
      primaryText={isEditMode ? 'Guardar' : 'Agregar'}
      primaryVariant='primary'
      onPrimaryAction={handleSubmit}
      primaryActionDisabled={isLoading || isLoadingMetadata}
      secondaryText='Cancelar'
      secondaryVariant='contained'
      onSecondaryAction={handleClose}
      showOverlay={true}
    >
      <Card margin={{ top: '4' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          {/* Nombre */}
          <Input
            label="Nombre *"
            name="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLoading || isLoadingMetadata}
            size="md"
          />

          <Input
            label={`${fiscalId.label} *`}
            name="cuit"
            type="text"
            value={cuit}
            onChange={(e) => handleCuitChange(e.target.value)}
            disabled={isEditMode || isLoading || isLoadingMetadata}
            size="md"
          />

          {/* Cantidad de acciones */}
          <Input
            label="Cantidad de acciones *"
            name="share_quantity"
            type="number"
            value={shareQuantity}
            onChange={(e) => setShareQuantity(e.target.value)}
            disabled={isLoading || isLoadingMetadata}
            size="md"
          />

          {/* Rol jerárquico */}
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Rol jerárquico *
            </P>
            <Card margin={{ top: '2' }}>
              <DropDown
                id="hierarchy_level"
                title={getSelectedHierarchyLevelName()}
                items={hierarchyLevelItems}
                onChange={handleHierarchyLevelChange}
                size="md"
                disabled={isLoadingMetadata}
              />
            </Card>
          </Card>
        </div>
      </Card>
    </Modal>
  );
};

export default NewPartnerModal;
